import { useCallback, useState } from 'react'
import type maplibregl from 'maplibre-gl'
import { MapPin } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { MapContainer, LocationMarker, SITE_ZOOM } from 'features/Map'
import PopupCloseButton from 'components/PopupCloseButton'
import useIsMobile from 'hooks/useIsMobile'

export interface MapLocation {
	latitude: number
	longitude: number
	assetName: string
	address: string
	city: string
	stateProvince: string
	image?: string
	retailer?: string
}

interface MapLocationPopupCNProps {
	open: boolean
	onClose: () => void
	apiKey?: string
	location: MapLocation
}

const MapLocationPopupCN = ({ open, onClose, location }: MapLocationPopupCNProps) => {
	const isMobile = useIsMobile()
	const [map, setMap] = useState<maplibregl.Map | null>(null)

	const handleMapLoad = useCallback((m: maplibregl.Map) => {
		setMap(m)
	}, [])

	const title = location.retailer
		? `${location.retailer} - ${location.assetName}`
		: location.assetName

	const subtitle = `${location.address}, ${location.city}, ${location.stateProvince}`

	const center: [number, number] = [location.longitude, location.latitude]

	const markerLocation = {
		id: 'popup-location',
		assetName: location.assetName,
		address: location.address,
		city: location.city,
		stateProvince: location.stateProvince,
		latitude: location.latitude,
		longitude: location.longitude,
		image: location.image,
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent hideClose className="relative max-w-4xl p-0 overflow-hidden">
				<DialogHeader className="px-5 pt-5 pb-3">
					{/* Until now this popup closed only on Escape or a backdrop click —
					    no visible control at any width, and no Escape key on a phone. */}
					<PopupCloseButton onClose={onClose} className="absolute top-3 right-3 z-10" />
					<div className="flex items-center gap-3 pr-14">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<MapPin className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>{title}</DialogTitle>
							<DialogDescription>{subtitle}</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 pb-5">
					<div
						className={
							/*
							 * 540px fixed would overflow a phone: the dialog caps at
							 * max-h-[90vh], which on an iPhone 14 is 760px against roughly
							 * 664px of visible content, pushing the map's own controls out
							 * of reach. svh sizes against the viewport WITH the browser
							 * chrome showing, so the bottom stays reachable.
							 */
							isMobile
								? 'rounded-lg overflow-hidden border border-gray-200 h-[55svh] min-h-[260px]'
								: 'rounded-lg overflow-hidden border border-gray-200 h-[540px]'
						}
					>
						<MapContainer center={center} zoom={SITE_ZOOM} onMapLoad={handleMapLoad}>
							<LocationMarker map={map} location={markerLocation} />
						</MapContainer>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default MapLocationPopupCN
